import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Container,
  Paper,
  Rating,
  Avatar,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Build as BuildIcon,
  CleaningServices as CleanIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SectionContainer from '../components/SectionContainer';
import ServiceAreaMap from '../components/ServiceAreaMap';
import { services, reviews, trustIndicators, howItWorksSteps } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const getIcon = (index) => {
    const icons = [ScheduleIcon, MoneyIcon, BuildIcon, CleanIcon];
    const IconComponent = icons[index % icons.length];
    return <IconComponent sx={{ fontSize: 40, color: 'secondary.main' }} />;
  };

  return (
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: '#fdfaf1',
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Box
                component="img"
                src="/images/chato_hs_logo.png"
                alt="Chato Home Services"
                sx={{ height: { xs: 120, sm: 150, md: 180 }, width: 'auto', maxWidth: '100%', display: 'block' }}
              />
            </Box>
          </Box>

          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            {t('home.heroTitle')}
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
            {t('home.heroSubtitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/contact')} sx={{ px: 4, py: 2, fontSize: '1.1rem', fontWeight: 600 }}>
              {t('home.requestQuote')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="tel:555-555-5555"
              startIcon={<PhoneIcon />}
              sx={{ px: 4, py: 2, fontSize: '1.1rem', fontWeight: 600, borderColor: 'white', color: 'white', '&:hover': { borderColor: 'secondary.main', backgroundColor: 'rgba(212, 175, 55, 0.1)' } }}
            >
              {t('home.callButton')}
            </Button>
          </Box>
        </Container>
      </Box>

      <SectionContainer backgroundColor="grey.50">
        <Grid container spacing={4}>
          {trustIndicators.map((indicator, index) => (
            <Grid item xs={12} sm={6} md={3} key={indicator.key}>
              <Box sx={{ textAlign: 'center' }}>
                {getIcon(index)}
                <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  {t(`trustIndicators.${indicator.key}.title`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`trustIndicators.${indicator.key}.description`)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      {/* Student Repairs Banner */}
      <SectionContainer backgroundColor="secondary.main">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
              {t('home.studentBannerTitle')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main' }}>
              {t('home.studentBannerText')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/students')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              {t('home.studentBannerButton')}
            </Button>
          </Grid>
        </Grid>
      </SectionContainer>

      <SectionContainer>
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('home.popularServices')}
        </Typography>
        <Grid container spacing={4}>
          {services.slice(0, 6).map((service) => {
            const title = t(`servicesData.${service.key}.title`);
            const description = t(`servicesData.${service.key}.description`);
            return (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <Card
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}
                  onClick={() => navigate('/services')}
                >
                  <CardMedia component="img" height="200" image={service.imageUrls[0]} alt={title} />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={service.avgRating} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary">
                        ({service.reviewCount} {t('home.reviewsSuffix')})
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="outlined" size="large" onClick={() => navigate('/services')} sx={{ px: 4 }}>
            {t('home.viewAllServices')}
          </Button>
        </Box>
      </SectionContainer>

      <SectionContainer backgroundColor="grey.50">
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('home.howItWorks')}
        </Typography>
        <Grid container spacing={4}>
          {howItWorksSteps.map((step) => (
            <Grid item xs={12} md={4} key={step.step}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, fontSize: '2rem', fontWeight: 700 }}
                >
                  {step.step}
                </Box>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {t(`howItWorks.${step.key}.title`)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t(`howItWorks.${step.key}.description`)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      <SectionContainer>
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          {t('home.areasWeServe')}
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: 'auto', fontWeight: 400 }}>
          {t('home.areasWeServeSubtitle')}
        </Typography>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <ServiceAreaMap />
        </Box>
      </SectionContainer>

      <SectionContainer>
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('home.customersSay')}
        </Typography>
        <Grid container spacing={4}>
          {reviews.slice(0, 3).map((review) => (
            <Grid item xs={12} md={4} key={review.id}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                    {review.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {review.name}
                    </Typography>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ flexGrow: 1, fontStyle: 'italic' }}>
                  "{t(`reviewsData.${review.key}`)}"
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      <SectionContainer backgroundColor="primary.main" sx={{ color: 'white' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {t('home.readyTitle')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('home.readySubtitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/contact')} sx={{ px: 4, py: 2, fontSize: '1.1rem' }}>
              {t('home.requestQuote')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="tel:555-555-5555"
              startIcon={<PhoneIcon />}
              sx={{ px: 4, py: 2, fontSize: '1.1rem', borderColor: 'white', color: 'white', '&:hover': { borderColor: 'secondary.main', backgroundColor: 'rgba(212, 175, 55, 0.1)' } }}
            >
              {t('home.callNow')}
            </Button>
          </Box>
        </Box>
      </SectionContainer>
    </Box>
  );
};

export default Home;
